import { errorToast } from "@/components/ui/toast.tsx";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { SettingsRow } from "./SettingsRow.tsx";

type EmailTopicSubscription = {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  type: "drip" | "recurring" | "oneoff";
  subscription: {
    status: "subscribed" | "unsubscribed" | "completed" | null;
    currentStep: number;
    subscribedAt: string | null;
  } | null;
};

const emailTopicsQueries = {
  all: () => ["userEmailTopics"] as const,
  list: () =>
    queryOptions({
      queryKey: [...emailTopicsQueries.all(), "list"] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<{ topics: EmailTopicSubscription[] }>(
          "/api/a/users/me/email-topics",
          {
            signal,
          },
        );
        return response.data.topics;
      },
    }),
};

const useUnsubscribe = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => {
      await api.post(`/api/a/users/me/email-topics/${slug}/unsubscribe`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTopicsQueries.all() });
    },
  });
};

const useResubscribe = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => {
      await api.post(`/api/a/users/me/email-topics/${slug}/resubscribe`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTopicsQueries.all() });
    },
  });
};

const TOPIC_ORDER = ["tutorial", "weekly-update", "personal-update"];

const topicOrderIndex = (slug: string) => {
  const idx = TOPIC_ORDER.indexOf(slug);
  return idx === -1 ? Number.POSITIVE_INFINITY : idx;
};

export const EmailSubscriptions = () => {
  const { data: topics, isPending, error } = useQuery(emailTopicsQueries.list());
  const unsubscribeMutation = useUnsubscribe();
  const resubscribeMutation = useResubscribe();

  const handleToggle = (topic: EmailTopicSubscription, nextChecked: boolean) => {
    const action = nextChecked ? resubscribeMutation : unsubscribeMutation;
    const errorCopy = nextChecked
      ? "Failed to subscribe. Please try again."
      : "Failed to unsubscribe. Please try again.";

    action.mutate(topic.slug, {
      onError: () => errorToast(errorCopy),
    });
  };

  if (isPending) {
    return <div className="py-4 text-sm text-muted-foreground">Loading email subscriptions…</div>;
  }

  if (error) {
    return <div className="py-4 text-sm text-destructive">Failed to load email subscriptions</div>;
  }

  if (!topics || topics.length === 0) {
    return null;
  }

  const isLoading = unsubscribeMutation.isPending || resubscribeMutation.isPending;
  const orderedTopics = [...topics].sort(
    (a, b) => topicOrderIndex(a.slug) - topicOrderIndex(b.slug),
  );

  return (
    <>
      {orderedTopics.map((topic) => {
        const status = topic.subscription?.status ?? null;
        const isCompleted = status === "completed";
        const isSubscribed = status === "subscribed";

        return (
          <SettingsRow
            key={topic.id}
            title={topic.name}
            description={topic.description ?? ""}
            checked={isSubscribed || isCompleted}
            disabled={isLoading || isCompleted}
            hint={isCompleted ? "Completed" : undefined}
            onCheckedChange={(next) => handleToggle(topic, next)}
          />
        );
      })}
    </>
  );
};
